export function copyToClipboard(content: string, { isPlainText = false } = {}): void {
  if (isPlainText) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(content).catch(err => {
        console.error('Failed to copy content: ', err);
      });
    }

    return;
  }

  const styles = `
    <style>
      * { margin: 0; font-family: sans-serif; }
    </style>
  `;

  // Check if the clipboard API is available
  if (navigator.clipboard && window.ClipboardItem) {
    const item = new ClipboardItem({
      'text/html': new Blob([`${styles}${content}`], { type: 'text/html' }),
      'text/plain': new Blob([convertHtmlToPlainText(content)], { type: 'text/plain' })
    });

    // Write the content to the clipboard
    navigator.clipboard.write([item]).catch(err => {
      console.error('Failed to copy content: ', err);
    });
  } else {
    copyToClipboardIE(content);
  }
}

function copyToClipboardIE(htmlContent: string) {
  // Create a temporary container to hold the HTML content
  const container = document.createElement('div');
  container.innerHTML = htmlContent;

  // Append the container to the body (hidden)
  container.style.position = 'fixed';
  container.style.pointerEvents = 'none';
  container.style.opacity = '0';
  document.body.appendChild(container);

  // Create a range and select the content
  const range = document.createRange();
  range.selectNode(container);

  // Get the selection object and clear previous selections
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);

  try {
    // Execute the copy command
    const successful = document.execCommand('copy');

    if (!successful) {
      console.error('Failed to copy content.');
    }
  } catch (err) {
    console.error('Failed to copy content: ', err);
  }

  // Cleanup: remove the container and clear the selection
  document.body.removeChild(container);
  selection?.removeAllRanges();
}

export function convertHtmlToPlainText(htmlContent: string) {
  // Create a temporary element to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  function getOrderedMarker(index: number, depth: number): string {
    const orderedStyles = ['decimal', 'lower-alpha', 'lower-roman'];
    const style = orderedStyles[Math.min(depth, orderedStyles.length - 1)];

    if (style === 'lower-alpha') {
      return `${toAlphabetic(index)}. `;
    }

    if (style === 'lower-roman') {
      return `${toRoman(index + 1)}. `;
    }

    return `${index + 1}. `;
  }

  function getUnorderedMarker(depth: number): string {
    const unorderedStyles = ['•', '○', '▪'];
    return `${unorderedStyles[Math.min(depth, unorderedStyles.length - 1)]} `;
  }

  function toAlphabetic(num: number): string {
    let result = '';
    while (num >= 0) {
      result = String.fromCharCode((num % 26) + 97) + result; // Convert to a-z
      num = Math.floor(num / 26) - 1; // Continue for aa, ab, etc.
    }
    return result;
  }

  function toRoman(num: number): string {
    const romanNumerals = [
      'I',
      'II',
      'III',
      'IV',
      'V',
      'VI',
      'VII',
      'VIII',
      'IX',
      'X',
      'XI',
      'XII',
      'XIII',
      'XIV',
      'XV',
      'XVI',
      'XVII',
      'XVIII',
      'XIX',
      'XX'
    ];
    return romanNumerals[num - 1] || num.toString();
  }

  function processList(listElement: Element, depth = 0, prefix = ''): string {
    let output = '';
    const isOrderedList = listElement.tagName === 'OL';
    const listItems = Array.from(listElement.children);

    for (let index = 0; index < listItems.length; index += 1) {
      const listItem = listItems[index];
      const listItemPrefix = isOrderedList
        ? `${prefix}${getOrderedMarker(index, depth)}`
        : `${prefix}${getUnorderedMarker(depth)}`;

      const listItemText = listItem.childNodes[0]?.textContent?.trim() ?? '';

      output += `${listItemPrefix}${listItemText}\n`;

      const nestedList = listItem.querySelector('ul, ol');

      if (nestedList) {
        output += processList(nestedList, depth + 1, `${prefix}  `);
      }
    }

    return output;
  }

  // Traverse the HTML content and convert lists to plain text
  let plainText = '';
  const elements = tempDiv.childNodes;
  elements.forEach(node => {
    if (node instanceof Element && (node.tagName === 'OL' || node.tagName === 'UL')) {
      plainText += `${processList(node)}`;
    } else {
      plainText += `${(node.textContent ?? '').trim()}\n`;
    }
  });

  return plainText.trim();
}
